import { createContext, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

const translations = {
  en: {
    nav: {
      project: "The Project",
      investment: "Investment",
      location: "Location",
      development: "The Development",
      tour: "360° Tour",
      faq: "FAQ",
      contact: "Contact",
    },
    hero: {
      h1: "Beachfront Condos for Sale in Huatulco, Mexico",
      title: "Invest by the Ocean in One of the Most Promising Destinations on Mexico's Pacific Coast",
      subtitle: "Designed for those looking to combine real estate investment, quality of life, and extended stays by the Pacific Ocean.",
      points: [
        "Less than 2 minutes walking distance to Playa Chahué",
        "Modern beach condos starting at $4,500,000 MXN",
        "Estimated return potential between 15% and 35%",
      ],
      ctaWhatsApp: "Chat on WhatsApp",
      ctaCall: "Request a Call",
    },
    discover: {
      label: "The Project",
      title: "Discover Paseo Chahué",
      subtitle: "A residential development designed to combine investment opportunity, comfort, and a privileged location in Bahías de Huatulco.",
      architecture: "Contemporary Architecture",
      condos: "55 Residential Condominiums",
      oceanViews: "Ocean Views",
      modernDesign: "Modern Design",
      primeLocation: "Prime Location",
      stepsBeach: "Steps from the Beach",
      coastalLiving: "Coastal Living",
      commercialSpaces: "10 Commercial Spaces",
      virtualTour: "360° Virtual Tour",
      locationLabel: "Prime Location",
      locationTitle: "Steps from Chahué Beach",
      locationDesc: "Walking distance to the beach, marina, restaurants, supermarkets, and essential services.",
      badge1: "2 min to Playa Chahué",
      badge2: "Marina nearby",
    },
    investmentLifestyle: {
      whyLabel: "Why Paseo Chahué",
      investmentTitle: "Investment",
      investmentDesc: "Position yourself in one of Mexico's most promising coastal destinations. Pre-construction pricing offers real appreciation potential as Huatulco continues to attract international buyers.",
      investmentPoints: [
        "Pre-construction pricing with appreciation potential",
        "Growing tourism market in a protected destination",
        "Diversify your portfolio with coastal real estate",
        "Strong vacation rental demand year-round",
      ],
      lifestyleTitle: "Lifestyle",
      lifestyleDesc: "Imagine a life where every day feels like a vacation. Warm weather year-round, pristine beaches steps away, and a community designed for those who value quality of life above all else.",
      lifestylePoints: [
        "Wake up to ocean and mountain views",
        "Walking distance to beaches and everyday modern amenities",
        "Relaxed pace of life surrounded by nature",
        "Extended stays or seasonal visits with comfort",
        "15 min drive to Huatulco International Airport",
      ],
    },
    playaChahue: {
      label: "The Beach",
      title: "Playa Chahué",
      desc1: "Golden sand, calm waters, and a Blue Flag certified coastline just steps from your door. Chahué offers the natural quality of life that defines Huatulco.",
      desc2: "With 300+ days of sunshine, marina access, beach clubs, and restaurants within walking distance, every day feels like the vacation you deserve.",
      stats: [
        { value: "300+", label: "Days of sunshine per year" },
        { value: "Blue Flag", label: "Certified beach" },
        { value: "2 min", label: "Walk to the beach" },
        { value: "9 Bays", label: "36 beaches to explore" },
      ],
    },
    destination: {
      label: "The Destination",
      title: "Huatulco: One of the Most Attractive Destinations on Mexico's Pacific Coast",
      p1: "Bahías de Huatulco is recognized for its unique combination of natural beauty, modern infrastructure, and high quality of life.",
      p2: "The region includes 9 bays and 36 beaches, many of which are located within protected natural areas that preserve the environment and limit excessive urban development.",
      p3: "Unlike many other coastal destinations, Huatulco maintains a balanced and well-planned urban structure that allows residents to enjoy the ocean, nature, and tranquility year-round.",
      p4: "With an international airport, modern services, and a warm climate throughout the year, Huatulco is increasingly attracting buyers from Mexico, Canada, and the United States.",
      stats: [
        { value: "+17%", label: "Annual Tourism Growth", note: "Year-over-year visitor increase" },
        { value: "80%", label: "Protected Territory", note: "National park & ecological reserves" },
        { value: "-50%", label: "Cost vs Cancún/Tulum", note: "More value at a fraction of the price" },
        { value: "3rd", label: "Fastest Growing Coastal Destination", note: "Among Mexico's Pacific coast" },
        { value: "12+", label: "New International Routes", note: "Expanding airport connectivity" },
      ],
    },
    development: {
      label: "The Development",
      title: "A Residential Development Designed for Living and Investing",
      p1: "Paseo Chahué is a residential project currently under development designed to offer comfort, functionality, and a prime location within Bahías de Huatulco.",
      p2: "The development combines contemporary architecture, quality materials, and efficient layouts designed for both second-home owners and investors.",
      stats: [
        { value: "55", label: "55 residential condominiums" },
        { value: "10", label: "10 commercial spaces" },
        { value: "30%", label: "30% confirmed construction progress" },
        { value: "38%", label: "38% of units already sold" },
      ],
      interiors: ["Kitchen & Dining", "Living Room", "Master Bedroom", "Bedroom with View"],
      tourLabel: "Interactive 360° Tours",
      tourTitle: "Explore Every Room",
      tourTabs: ["Living Room", "Bedroom", "Kitchen", "Terrace"],
    },
    backing: {
      label: "Trust & Experience",
      title: "A Project Supported by Experienced Companies",
      desc: "Paseo Chahué is developed and built by teams with experience in residential construction and real estate projects in Bahías de Huatulco, providing confidence to buyers investing during the construction phase.",
      developer: "Developer",
      construction: "Construction",
      constructionDesc: "More than 15 years of experience building residential properties in Huatulco.",
      sales: "Sales Representation",
      salesDesc: "Master broker representing the project.",
      legal: "Legal Advisory",
      legalDesc: "Legal guidance from professionals specialized in real estate law.",
    },
    purchase: {
      label: "How It Works",
      title: "Buying Property in Mexico is a Clear and Secure Process",
      p1: "Foreign buyers can purchase property in coastal areas of Mexico through a bank trust structure (fideicomiso), which allows full ownership rights, including the ability to sell, rent, or transfer the property.",
      p2: "Throughout the entire process, the team at Blackstone Real Estate Huatulco assists buyers step by step to ensure a smooth and transparent experience.",
      steps: [
        { title: "Request Information", desc: "Request project information" },
        { title: "Consultation", desc: "Personalized consultation with a sales advisor" },
        { title: "Reservation", desc: "Reservation of the selected unit" },
        { title: "Agreement", desc: "Signing of the purchase agreement with legal guidance" },
        { title: "Updates", desc: "Updates during the construction phase" },
        { title: "Delivery", desc: "Delivery of the property upon project completion" },
      ],
    },
    opportunity: {
      label: "The Opportunity",
      title: "An Investment Opportunity During the Pre-Construction Phase",
      p1: "Purchasing property during construction allows buyers to access preferential pricing before the project is completed.",
      p2: "As construction progresses and the development approaches completion, property values typically adjust accordingly. For this reason, many buyers choose to secure their unit during early phases of the project.",
      p3: "In addition, owners can benefit from Huatulco's growing vacation rental market, with professional property management companies available to handle the entire rental process if desired.",
      stats: [
        { value: "30%", label: "Construction Progress" },
        { value: "38%", label: "Units Sold" },
        { value: "$4.5M", label: "Starting Price (MXN)" },
      ],
      ctaWhatsApp: "Chat on WhatsApp",
      ctaCall: "Request a Call",
    },
    faq: {
      label: "Questions & Answers",
      title: "Frequently Asked Questions About Paseo Chahué",
      questions: [
        { q: "Can foreigners buy property in Mexico?", a: "Yes. Foreign buyers can acquire property in coastal areas through a bank trust structure that provides full ownership rights." },
        { q: "Is the project already under construction?", a: "Yes. Paseo Chahué is currently under construction with approximately 30% progress." },
        { q: "How many units are in the development?", a: "The project includes 55 residential condominiums and 10 commercial spaces." },
        { q: "What is the starting price of the units?", a: "Condominiums currently start from 4.5 million Mexican pesos, depending on size and location within the development." },
        { q: "Can I rent my property when I am not using it?", a: "Yes. Huatulco has a strong vacation rental market, and professional management companies can handle the rental process on behalf of owners." },
      ],
    },
    contact: {
      label: "Get In Touch",
      title: "Request Availability and Updated Pricing",
      desc: "Our team can provide detailed information about available units, pricing, and purchase options.",
      ctaWhatsApp: "Chat with an Advisor on WhatsApp",
      or: "or fill out the form",
    },
    whatsappModal: {
      title: "Chat with an Advisor",
      subtitle: "We'll contact you via WhatsApp",
    },
    whyHuatulco: {
      label1: "The Opportunity",
      title1: "Why Huatulco Is Not Just Another Beach Destination",
      subtitle1: "The numbers tell the story of a destination on the rise — with strong fundamentals and untapped potential.",
      stats: [
        { value: "+17%", label: "Annual Tourism Growth", note: "Year-over-year visitor increase" },
        { value: "80%", label: "Protected Territory", note: "National park & ecological reserves" },
        { value: "-50%", label: "Cost vs Cancún/Tulum", note: "More value at a fraction of the price" },
        { value: "3rd", label: "Fastest Growing Coastal Destination", note: "Among Mexico's Pacific coast" },
        { value: "12+", label: "New International Routes", note: "Expanding airport connectivity" },
      ],
      label2: "The Advantage",
      title2: "An Inefficient Market Creates Opportunity",
      subtitle2: "While destinations like Tulum and Cancún are oversaturated, Huatulco remains under the radar — offering better value, lower competition, and stronger long-term appreciation potential for early investors.",
      marketPoints: [
        { title: "Low Competition", desc: "Fewer developments mean less price pressure and higher margins for early buyers." },
        { title: "Untapped Demand", desc: "International buyers are discovering Huatulco, but inventory hasn't caught up yet." },
        { title: "Growing Infrastructure", desc: "New flights, roads, and services are increasing accessibility and property values." },
      ],
      label3: "Planned by Design",
      title3: "A Destination Built for Long-Term Value",
      subtitle3: "Huatulco was master-planned by FONATUR — the same federal agency behind Cancún — with strict ecological and urban regulations that protect property values.",
      planningPoints: [
        { title: "FONATUR Master Plan", desc: "Federal-level urban planning ensures controlled, sustainable growth." },
        { title: "80% Ecological Reserve", desc: "Protected land limits overdevelopment and preserves natural beauty." },
        { title: "Strict Building Codes", desc: "Height and density regulations maintain the destination's exclusivity." },
      ],
      conclusion: "Huatulco isn't just a place to buy property — it's a strategic investment in one of Mexico's last unspoiled coastal destinations.",
    },
    footer: {
      location: "Bahías de Huatulco, Oaxaca, Mexico",
      copyright: "© 2025 Paseo Chahué. All rights reserved.",
    },
  },
  es: {
    nav: {
      project: "El Proyecto",
      investment: "Inversión",
      location: "Ubicación",
      development: "El Desarrollo",
      tour: "Tour 360°",
      faq: "Preguntas",
      contact: "Contacto",
    },
    hero: {
      h1: "Condos en Venta Frente al Mar en Huatulco, México",
      title: "Invierte Junto al Océano en Uno de los Destinos Más Prometedores de la Costa del Pacífico Mexicano",
      subtitle: "Diseñado para quienes buscan combinar inversión inmobiliaria, calidad de vida y estadías prolongadas junto al Océano Pacífico.",
      points: [
        "A menos de 2 minutos caminando de Playa Chahué",
        "Condominios de playa desde $4,500,000 MXN",
        "Potencial de retorno estimado entre 15% y 35%",
      ],
      ctaWhatsApp: "Chatea por WhatsApp",
      ctaCall: "Solicitar una Llamada",
    },
    discover: {
      label: "El Proyecto",
      title: "Descubre Paseo Chahué",
      subtitle: "Un desarrollo residencial diseñado para combinar oportunidad de inversión, confort y una ubicación privilegiada en Bahías de Huatulco.",
      architecture: "Arquitectura Contemporánea",
      condos: "55 Condominios Residenciales",
      oceanViews: "Vistas al Mar",
      modernDesign: "Diseño Moderno",
      primeLocation: "Ubicación Premium",
      stepsBeach: "A pasos de la Playa",
      coastalLiving: "Vida Costera",
      commercialSpaces: "10 Locales Comerciales",
      virtualTour: "Tour Virtual 360°",
      locationLabel: "Ubicación Premium",
      locationTitle: "A pasos de Playa Chahué",
      locationDesc: "A distancia caminable de la playa, marina, restaurantes, supermercados y servicios esenciales.",
      badge1: "2 min a Playa Chahué",
      badge2: "Marina cercana",
    },
    investmentLifestyle: {
      whyLabel: "Por qué Paseo Chahué",
      investmentTitle: "Inversión",
      investmentDesc: "Posiciónate en uno de los destinos costeros más prometedores de México. Los precios de pre-construcción ofrecen un potencial real de plusvalía mientras Huatulco sigue atrayendo compradores internacionales.",
      investmentPoints: [
        "Precios de pre-construcción con potencial de plusvalía",
        "Mercado turístico en crecimiento en un destino protegido",
        "Diversifica tu portafolio con bienes raíces costeros",
        "Fuerte demanda de renta vacacional todo el año",
      ],
      lifestyleTitle: "Estilo de Vida",
      lifestyleDesc: "Imagina una vida donde cada día se siente como vacaciones. Clima cálido todo el año, playas prístinas a unos pasos y una comunidad diseñada para quienes valoran la calidad de vida por sobre todo.",
      lifestylePoints: [
        "Despierta con vistas al mar y la montaña",
        "A distancia caminable de playas y amenidades modernas",
        "Ritmo de vida relajado rodeado de naturaleza",
        "Estadías prolongadas o visitas estacionales con confort",
        "A 15 min del Aeropuerto Internacional de Huatulco",
      ],
    },
    playaChahue: {
      label: "La Playa",
      title: "Playa Chahué",
      desc1: "Arena dorada, aguas tranquilas y una costa certificada con Bandera Azul a solo pasos de tu puerta. Chahué ofrece la calidad de vida natural que define a Huatulco.",
      desc2: "Con más de 300 días de sol, acceso a la marina, clubes de playa y restaurantes a distancia caminable, cada día se siente como las vacaciones que mereces.",
      stats: [
        { value: "300+", label: "Días de sol al año" },
        { value: "Bandera Azul", label: "Playa certificada" },
        { value: "2 min", label: "Caminando a la playa" },
        { value: "9 Bahías", label: "36 playas por explorar" },
      ],
    },
    destination: {
      label: "El Destino",
      title: "Huatulco: Uno de los destinos más atractivos del Pacífico Mexicano",
      p1: "Bahías de Huatulco es reconocido por su combinación única de belleza natural, infraestructura moderna y alta calidad de vida.",
      p2: "La región incluye 9 bahías y 36 playas, muchas de las cuales están ubicadas dentro de áreas naturales protegidas que preservan el medio ambiente y limitan el desarrollo urbano excesivo.",
      p3: "A diferencia de muchos otros destinos costeros, Huatulco mantiene una estructura urbana equilibrada y bien planificada que permite a los residentes disfrutar del océano, la naturaleza y la tranquilidad durante todo el año.",
      p4: "Con un aeropuerto internacional, servicios modernos y un clima cálido todo el año, Huatulco atrae cada vez más compradores de México, Canadá y Estados Unidos.",
      stats: [
        { value: "+17%", label: "Crecimiento Turístico Anual", note: "Incremento interanual de visitantes" },
        { value: "80%", label: "Territorio Protegido", note: "Parque nacional y reservas ecológicas" },
        { value: "-50%", label: "Costo vs Cancún/Tulum", note: "Más valor a una fracción del precio" },
        { value: "3er", label: "Destino Costero de Mayor Crecimiento", note: "En la costa del Pacífico mexicano" },
        { value: "12+", label: "Nuevas Rutas Internacionales", note: "Conectividad aeroportuaria en expansión" },
      ],
    },
    development: {
      label: "El Desarrollo",
      title: "Un desarrollo residencial diseñado para vivir e invertir",
      p1: "Paseo Chahué es un proyecto residencial actualmente en desarrollo, diseñado para ofrecer confort, funcionalidad y una ubicación privilegiada dentro de Bahías de Huatulco.",
      p2: "El desarrollo combina arquitectura contemporánea, materiales de calidad y distribuciones eficientes diseñadas tanto para propietarios de segunda vivienda como para inversionistas.",
      stats: [
        { value: "55", label: "55 condominios residenciales" },
        { value: "10", label: "10 locales comerciales" },
        { value: "30%", label: "30% de avance de obra confirmado" },
        { value: "38%", label: "38% de unidades ya vendidas" },
      ],
      interiors: ["Cocina y Comedor", "Sala de Estar", "Recámara Principal", "Recámara con Vista"],
      tourLabel: "Tours Interactivos 360°",
      tourTitle: "Explora Cada Espacio",
      tourTabs: ["Sala", "Recámara", "Cocina", "Terraza"],
    },
    backing: {
      label: "Confianza y Experiencia",
      title: "Un proyecto respaldado por empresas con experiencia",
      desc: "Paseo Chahué es desarrollado y construido por equipos con experiencia en construcción residencial y proyectos inmobiliarios en Bahías de Huatulco, brindando confianza a los compradores que invierten durante la fase de construcción.",
      developer: "Desarrollador",
      construction: "Construcción",
      constructionDesc: "Más de 15 años de experiencia construyendo propiedades residenciales en Huatulco.",
      sales: "Representación Comercial",
      salesDesc: "Bróker maestro representando el proyecto.",
      legal: "Asesoría Legal",
      legalDesc: "Orientación legal de profesionales especializados en derecho inmobiliario.",
    },
    purchase: {
      label: "Cómo Funciona",
      title: "Comprar propiedad en México es un proceso claro y seguro",
      p1: "Los compradores extranjeros pueden adquirir propiedades en zonas costeras de México a través de una estructura de fideicomiso bancario, que otorga plenos derechos de propiedad, incluyendo la posibilidad de vender, rentar o transferir la propiedad.",
      p2: "Durante todo el proceso, el equipo de Blackstone Real Estate Huatulco asiste a los compradores paso a paso para garantizar una experiencia fluida y transparente.",
      steps: [
        { title: "Solicitar Información", desc: "Solicita información del proyecto" },
        { title: "Consulta", desc: "Consulta personalizada con un asesor de ventas" },
        { title: "Reservación", desc: "Reservación de la unidad seleccionada" },
        { title: "Contrato", desc: "Firma del contrato de compra con asesoría legal" },
        { title: "Actualizaciones", desc: "Actualizaciones durante la fase de construcción" },
        { title: "Entrega", desc: "Entrega de la propiedad al completarse el proyecto" },
      ],
    },
    opportunity: {
      label: "La Oportunidad",
      title: "Una oportunidad de inversión en fase de pre-construcción",
      p1: "Comprar una propiedad durante la construcción permite a los compradores acceder a precios preferenciales antes de que el proyecto esté terminado.",
      p2: "A medida que avanza la construcción y el desarrollo se acerca a su finalización, los valores de las propiedades se ajustan en consecuencia. Por esta razón, muchos compradores eligen asegurar su unidad durante las fases tempranas del proyecto.",
      p3: "Además, los propietarios pueden beneficiarse del creciente mercado de renta vacacional de Huatulco, con empresas de administración profesional disponibles para manejar todo el proceso de renta si lo desean.",
      stats: [
        { value: "30%", label: "Avance de Obra" },
        { value: "38%", label: "Unidades Vendidas" },
        { value: "$4.5M", label: "Precio Inicial (MXN)" },
      ],
      ctaWhatsApp: "Chatea por WhatsApp",
      ctaCall: "Solicitar una Llamada",
    },
    faq: {
      label: "Preguntas y Respuestas",
      title: "Preguntas frecuentes sobre Paseo Chahué",
      questions: [
        { q: "¿Pueden los extranjeros comprar propiedades en México?", a: "Sí. Los compradores extranjeros pueden adquirir propiedades en zonas costeras a través de una estructura de fideicomiso bancario que otorga plenos derechos de propiedad." },
        { q: "¿El proyecto ya está en construcción?", a: "Sí. Paseo Chahué se encuentra actualmente en construcción con aproximadamente un 30% de avance." },
        { q: "¿Cuántas unidades tiene el desarrollo?", a: "El proyecto incluye 55 condominios residenciales y 10 locales comerciales." },
        { q: "¿Cuál es el precio inicial de las unidades?", a: "Los condominios actualmente comienzan desde 4.5 millones de pesos mexicanos, dependiendo del tamaño y ubicación dentro del desarrollo." },
        { q: "¿Puedo rentar mi propiedad cuando no la esté usando?", a: "Sí. Huatulco tiene un fuerte mercado de renta vacacional y empresas de administración profesional pueden manejar el proceso de renta en nombre de los propietarios." },
      ],
    },
    contact: {
      label: "Contáctanos",
      title: "Solicita disponibilidad y precios actualizados",
      desc: "Nuestro equipo puede brindarte información detallada sobre unidades disponibles, precios y opciones de compra.",
      ctaWhatsApp: "Chatea con un Asesor por WhatsApp",
      or: "o completa el formulario",
    },
    whatsappModal: {
      title: "Chatea con un Asesor",
      subtitle: "Te contactaremos por WhatsApp",
    },
    whyHuatulco: {
      label1: "La Oportunidad",
      title1: "Por qué Huatulco no es solo otro destino de playa",
      subtitle1: "Los números cuentan la historia de un destino en ascenso — con fundamentos sólidos y potencial sin explotar.",
      stats: [
        { value: "+17%", label: "Crecimiento Turístico Anual", note: "Incremento interanual de visitantes" },
        { value: "80%", label: "Territorio Protegido", note: "Parque nacional y reservas ecológicas" },
        { value: "-50%", label: "Costo vs Cancún/Tulum", note: "Más valor a una fracción del precio" },
        { value: "3er", label: "Destino Costero de Mayor Crecimiento", note: "En la costa del Pacífico mexicano" },
        { value: "12+", label: "Nuevas Rutas Internacionales", note: "Conectividad aeroportuaria en expansión" },
      ],
      label2: "La Ventaja",
      title2: "Un mercado ineficiente crea oportunidad",
      subtitle2: "Mientras destinos como Tulum y Cancún están sobresaturados, Huatulco permanece bajo el radar — ofreciendo mejor valor, menor competencia y un mayor potencial de plusvalía a largo plazo para inversionistas tempranos.",
      marketPoints: [
        { title: "Baja Competencia", desc: "Menos desarrollos significan menor presión en precios y mejores márgenes para compradores tempranos." },
        { title: "Demanda Sin Explotar", desc: "Compradores internacionales están descubriendo Huatulco, pero el inventario aún no ha alcanzado la demanda." },
        { title: "Infraestructura en Crecimiento", desc: "Nuevos vuelos, carreteras y servicios están aumentando la accesibilidad y el valor de las propiedades." },
      ],
      label3: "Diseñado con Visión",
      title3: "Un destino construido para valor a largo plazo",
      subtitle3: "Huatulco fue planificado por FONATUR — la misma agencia federal detrás de Cancún — con regulaciones ecológicas y urbanas estrictas que protegen el valor de las propiedades.",
      planningPoints: [
        { title: "Plan Maestro FONATUR", desc: "Planificación urbana a nivel federal que asegura crecimiento controlado y sostenible." },
        { title: "80% Reserva Ecológica", desc: "El territorio protegido limita la sobreexplotación y preserva la belleza natural." },
        { title: "Códigos de Construcción Estrictos", desc: "Regulaciones de altura y densidad mantienen la exclusividad del destino." },
      ],
      conclusion: "Huatulco no es solo un lugar para comprar una propiedad — es una inversión estratégica en uno de los últimos destinos costeros vírgenes de México.",
    },
    footer: {
      location: "Bahías de Huatulco, Oaxaca, México",
      copyright: "© 2025 Paseo Chahué. Todos los derechos reservados.",
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = location.pathname.startsWith("/es") ? "es" : "en";
  const t = translations[lang];

  const setLang = (newLang) => {
    if (newLang === "es" && !location.pathname.startsWith("/es")) {
      navigate("/es");
    } else if (newLang === "en" && location.pathname.startsWith("/es")) {
      navigate("/");
    }
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
